import { User, Bot, Brain, Sparkles, ShieldCheck, Layers, ArrowRight, Zap, Clock, Database, Workflow, Cpu } from "lucide-react";

/* ============================================================
   DESIGN OPTIONS — "Manual Execution vs Agentic Orchestration"
   Brand: espresso #211307, terracotta #D1633D, amber #E0902B,
   cream #F7F5F0/#FBF9F5, sage #9CA888 (TRiSM)
   ============================================================ */

const Canvas = ({ label, children }) => (
  <div className="mb-20">
    <p className="mb-4 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#D1633D]">{label}</p>
    <div className="rounded-[26px] border border-[#211307]/10 bg-[#F4F1EB] p-8 lg:p-10 shadow-[0_30px_80px_-40px_rgba(33,19,7,0.35)]">
      {children}
    </div>
  </div>
);

/* ---------- OPTION A — Proportional Timeline ---------- */
const OptionA = () => {
  const manual = [
    { who: "John.M", d: "1 Day", w: "22%" },
    { who: "Lisa.T", d: "1 Day", w: "22%" },
    { who: "Tim.D", d: "2 Days", w: "34%" },
    { who: "Joe.L", d: "1 Day", w: "22%" },
  ];
  const agents = ["Context", "Reason", "Decision", "Human ✓"];
  return (
    <Canvas label="Option A · Proportional Timeline">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <p className="font-mono2 text-[10px] uppercase tracking-[0.28em] text-[#8A5730]/70">Request #11045</p>
          <h3 className="font-display text-xl text-[#211307] mt-1">Understand → Analyze → Act → Approve</h3>
        </div>
      </div>

      {/* Manual lane */}
      <div className="mb-9">
        <div className="flex items-center justify-between mb-2.5">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/50">Manual Execution</span>
          <span className="text-[13px] text-[#211307]/45">4 people · sequential</span>
        </div>
        <div className="flex gap-1.5 w-full">
          {manual.map((m) => (
            <div key={m.who} style={{ width: m.w }} className="group">
              <div className="h-11 rounded-lg bg-[#D8D2C7] border border-[#211307]/10 flex items-center justify-center gap-1.5 text-[#4A3A2A]">
                <User size={13} strokeWidth={2} />
                <span className="text-[12px] font-medium">{m.who}</span>
              </div>
              <p className="text-center mt-1.5 font-mono2 text-[10px] text-[#211307]/40">{m.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/40">Time to close</span>
          <span className="font-display text-2xl text-[#211307]">4.4 Days</span>
        </div>
      </div>

      {/* Agentic lane — dramatically shorter */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#D1633D]">Agentic Orchestration</span>
          <span className="text-[13px] text-[#211307]/45">4 agents · parallel</span>
        </div>
        <div className="flex items-center gap-2" style={{ width: "16%", minWidth: 300 }}>
          <div className="flex-1 flex gap-1.5">
            {agents.map((a) => (
              <div key={a} className="flex-1 h-11 rounded-lg bg-[#211307] flex items-center justify-center gap-1 text-[#F7F5F0]">
                <Bot size={12} className="text-[#E8AA68]" strokeWidth={2} />
                <span className="text-[10px] font-medium">{a}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 flex items-baseline gap-2">
          <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/40">Time to close</span>
          <span className="font-display text-2xl text-[#D1633D]">10 Min</span>
          <span className="ml-1 rounded-full bg-[#E0902B]/15 px-2 py-0.5 font-mono2 text-[10px] text-[#8A5730]">99.8% faster</span>
        </div>
      </div>

      {/* footer */}
      <div className="mt-9 pt-6 border-t border-[#211307]/10 flex items-center gap-3 flex-wrap">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/40">Powered by</span>
        <span className="font-display text-[#D1633D] text-lg">Agentic Alpha</span>
        <div className="flex items-center gap-2 ml-auto">
          {[["Context", Database], ["AI Agent", Bot], ["Intelligent App", Workflow], ["AI TRiSM", ShieldCheck]].map(([t, Icon]) => (
            <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-[#211307]/12 bg-white/60 px-3 py-1.5 text-[11px] text-[#4A3A2A]">
              <Icon size={12} className="text-[#D1633D]" strokeWidth={2} /> {t}
            </span>
          ))}
        </div>
      </div>
    </Canvas>
  );
};

/* ---------- OPTION B — Contrast Cards + Orchestration Graph ---------- */
const OptionB = () => (
  <Canvas label="Option B · Split Contrast + Orchestration Graph">
    <div className="grid lg:grid-cols-[1fr_1fr_auto] gap-6 items-stretch">
      {/* Manual — muted / desaturated */}
      <div className="rounded-2xl bg-[#E7E3DB] border border-[#211307]/10 p-5 grayscale-[0.15]">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#B9B1A3]" /><span className="w-2 h-2 rounded-full bg-[#B9B1A3]" />
          <span className="ml-1 text-[13px] font-semibold text-[#5A4C3C]">Traditional Application</span>
        </div>
        {[["John.M", "1 Day"], ["Lisa.T", "1 Day"], ["Tim.D", "2 Days"], ["Joe.L", "1 Day"]].map(([n, d]) => (
          <div key={n} className="flex items-center gap-3 py-2.5 border-b border-[#211307]/5 last:border-0">
            <span className="w-7 h-7 rounded-full bg-[#CFC8BB] flex items-center justify-center text-[#6B5B48]"><User size={14} /></span>
            <span className="text-[13px] text-[#4A3A2A] flex-1">{n}</span>
            <span className="rounded-md bg-[#D8D2C7] px-2.5 py-1 font-mono2 text-[10px] text-[#6B5B48]">{d}</span>
          </div>
        ))}
        <div className="mt-4">
          <p className="font-mono2 text-[9px] uppercase tracking-[0.24em] text-[#8A7C68]">Time to close</p>
          <p className="font-display text-3xl text-[#5A4C3C]">4.4 Days</p>
        </div>
      </div>

      {/* Agentic — dark, glowing orchestration */}
      <div className="relative rounded-2xl bg-[#211307] border border-[#E0902B]/25 p-5 overflow-hidden">
        <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#E0902B]/12 blur-3xl" />
        <div className="relative flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#E8AA68]/60" /><span className="w-2 h-2 rounded-full bg-[#E8AA68]/60" />
          <span className="ml-1 text-[13px] font-semibold text-[#F7F5F0]">Agentic Application</span>
          <span className="ml-auto rounded-full bg-[#E0902B]/15 px-2 py-0.5 font-mono2 text-[9px] text-[#E8AA68]">human-in-the-loop</span>
        </div>
        <div className="relative flex items-center gap-3">
          <div className="flex-1 space-y-2.5">
            {[["Context Agent", "2 Min"], ["Reason Agent", "4 Min"], ["Decision Agent", "1 Min"], ["Human Approval", "—"]].map(([n, d]) => (
              <div key={n} className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center text-[#E8AA68]"><Bot size={13} /></span>
                <span className="text-[12px] text-[#F7F5F0]/85 flex-1">{n}</span>
                <span className="rounded-md bg-white/[0.06] px-2 py-0.5 font-mono2 text-[10px] text-[#E8AA68]">{d}</span>
              </div>
            ))}
          </div>
          {/* orchestrator node */}
          <div className="shrink-0 flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-[#D1633D] flex items-center justify-center text-white shadow-[0_0_0_6px_rgba(224,144,43,0.15)]">
              <Sparkles size={20} />
            </div>
            <span className="mt-2 font-mono2 text-[9px] uppercase tracking-wide text-[#E8AA68]/70 text-center leading-tight">Orchestrator</span>
          </div>
        </div>
        <div className="relative mt-4">
          <p className="font-mono2 text-[9px] uppercase tracking-[0.24em] text-[#E8AA68]/60">Time to close</p>
          <p className="font-display text-3xl text-[#E8AA68]">10 Min</p>
        </div>
      </div>

      {/* Right power rail */}
      <div className="flex flex-col items-center justify-center gap-2 lg:w-44">
        <span className="font-mono2 text-[9px] uppercase tracking-[0.24em] text-[#211307]/40 mb-1">Powered by</span>
        {[["Context", "#8A5730", Database], ["AI Agent", "#D1633D", Bot], ["Intelligent App", "#B08D57", Workflow], ["AI TRiSM", "#9CA888", ShieldCheck]].map(([t, c, Icon], i) => (
          <div key={t} className="w-full flex flex-col items-center gap-2">
            <div className="w-full rounded-xl px-4 py-2.5 flex items-center gap-2 text-white text-[12px] font-medium" style={{ background: c }}>
              <Icon size={14} /> {t}
            </div>
            {i < 3 && <ArrowRight size={14} className="text-[#211307]/30 rotate-90" />}
          </div>
        ))}
        <span className="font-display text-[#D1633D] text-base mt-2">Agentic Alpha</span>
      </div>
    </div>
  </Canvas>
);

/* ---------- OPTION C — Unified Transformation Delta ---------- */
const OptionC = () => (
  <Canvas label="Option C · Transformation Delta">
    <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-center">
      {/* before mini */}
      <div className="rounded-2xl bg-[#EAE6DE] border border-[#211307]/10 p-6">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/45 mb-3">Before · Manual</p>
        <div className="space-y-2">
          {["Understand", "Analyze", "Act", "Approve"].map((s, i) => (
            <div key={s} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-[#D8D2C7] flex items-center justify-center font-mono2 text-[10px] text-[#6B5B48]">{i + 1}</span>
              <span className="text-[13px] text-[#4A3A2A]">{s}</span>
              <span className="ml-auto h-1.5 rounded-full bg-[#C9C1B3]" style={{ width: [70, 70, 100, 55][i] }} />
            </div>
          ))}
        </div>
        <p className="mt-5 font-display text-2xl text-[#5A4C3C]">4.4 Days</p>
      </div>

      {/* center delta */}
      <div className="flex flex-col items-center px-2">
        <div className="w-14 h-14 rounded-full bg-[#211307] flex items-center justify-center text-[#E8AA68] shadow-[0_0_0_6px_rgba(224,144,43,0.12)]">
          <Zap size={24} />
        </div>
        <div className="my-3 flex items-center gap-2 text-[#211307]/40"><Clock size={13} /><ArrowRight size={16} className="text-[#D1633D]" /></div>
        <span className="rounded-full bg-[#D1633D] text-white px-3 py-1 font-mono2 text-[11px]">99.8% faster</span>
      </div>

      {/* after mini — warm */}
      <div className="relative rounded-2xl bg-[#211307] border border-[#E0902B]/25 p-6 overflow-hidden">
        <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full bg-[#E0902B]/12 blur-3xl" />
        <p className="relative font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#E8AA68] mb-3">After · Agentic</p>
        <div className="relative space-y-2">
          {[["Context Agent", Bot], ["Reason Agent", Brain], ["Decision Agent", Cpu], ["Human Approval", User]].map(([s, Icon]) => (
            <div key={s} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-lg bg-white/[0.07] flex items-center justify-center text-[#E8AA68]"><Icon size={13} /></span>
              <span className="text-[13px] text-[#F7F5F0]/85">{s}</span>
              <span className="ml-auto h-1.5 w-3 rounded-full bg-[#E0902B]" />
            </div>
          ))}
        </div>
        <p className="relative mt-5 font-display text-2xl text-[#E8AA68]">10 Min</p>
      </div>
    </div>

    {/* Agentic Alpha ribbon */}
    <div className="mt-8 pt-6 border-t border-[#211307]/10 flex items-center justify-center gap-3 flex-wrap">
      <span className="font-mono2 text-[10px] uppercase tracking-[0.24em] text-[#211307]/40">Powered by</span>
      <span className="font-display text-[#D1633D] text-lg mr-2">Agentic Alpha</span>
      {[["Context", Database], ["AI Agent", Bot], ["Intelligent App", Workflow], ["AI TRiSM", ShieldCheck]].map(([t, Icon], i) => (
        <span key={t} className="inline-flex items-center gap-1.5">
          {i > 0 && <span className="text-[#211307]/20">·</span>}
          <span className="inline-flex items-center gap-1.5 text-[12px] text-[#4A3A2A]"><Icon size={13} className="text-[#D1633D]" /> {t}</span>
        </span>
      ))}
    </div>
  </Canvas>
);

export default function HeroVisualOptions() {
  return (
    <div className="min-h-screen bg-[#EEEAE2] py-16 px-6 lg:px-14">
      <div className="max-w-5xl mx-auto">
        <h1 className="font-display text-3xl text-[#211307] mb-2">Comparison Visual — Design Options</h1>
        <p className="text-[#211307]/55 mb-12">Three directions for the Manual vs Agentic story. Pick one to finalize.</p>
        <OptionA />
        <OptionB />
        <OptionC />
      </div>
    </div>
  );
}
